import { useMemo } from "react";
import { Link } from "react-router-dom";
import { trpc } from "@/lib/trpc";
import { useAuth, hasUserAuth } from "@/lib/auth";
import { formatRelativeTime } from "@/lib/freshness";
import { cliApiUrl } from "@/lib/cli-api-url";
import { NoProjectsOnboarding } from "@/components/project/no-projects-onboarding";
import { LimitReachedBanner } from "@/components/limit-reached-banner";
import { Page } from "@/components/layout/page";
import { ActionBar } from "@/components/layout/action-bar";
import { Timestamp } from "@/components/ui/timestamp";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle, CheckCircle2, Play, Plus, Route } from "lucide-react";
import {
  Card,
  CardFooter,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";

const EMPTY_LIST_POLL_MS = 5_000;

export function ProjectsPage() {
  const { auth } = useAuth();
  const isMultiUser = hasUserAuth(auth);

  const {
    data: projects,
    isLoading,
    isError,
    refetch,
  } = trpc.project.list.useQuery(undefined, {
    // A newcomer's first project arrives from `iamu auth login` / `iamu sync`
    // in their terminal, not from this page. Poll while the list is empty so
    // it appears without a reload; focus refetch covers switching back.
    refetchInterval: (query) =>
      query.state.data?.length === 0 ? EMPTY_LIST_POLL_MS : false,
    refetchOnWindowFocus: true,
  });

  const { data: planSummary } = trpc.usage.planSummary.useQuery(undefined, {
    enabled: isMultiUser,
  });

  const isProjectLimitReached = Boolean(
    planSummary?.projects &&
      !planSummary.projects.unlimited &&
      planSummary.projects.used >= planSummary.projects.limit,
  );

  const { owned, shared } = useMemo(() => {
    if (!projects) return { owned: [], shared: [] };
    if (!isMultiUser) return { owned: projects, shared: [] };
    return {
      owned: projects.filter((p) => p.role === "owner"),
      shared: projects.filter((p) => p.role && p.role !== "owner"),
    };
  }, [projects, isMultiUser]);

  if (isLoading) {
    return (
      <Page title="Projects">
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </Page>
    );
  }

  if (isError) {
    return (
      <Page title="Projects">
        <div className="mx-auto max-w-md py-24 text-center">
          <AlertCircle className="mx-auto h-10 w-10 text-muted-foreground" />
          <h2 className="mt-4 text-lg font-semibold tracking-tight">
            Something went wrong
          </h2>
          <p className="mt-2 text-muted-foreground">
            Could not load your projects. Check your connection and try again.
          </p>
          <Button className="mt-6" size="lg" onClick={() => refetch()}>
            Try again
          </Button>
        </div>
      </Page>
    );
  }

  const newProjectAction = isProjectLimitReached ? (
    <SimpleTooltip
      content={`Project limit reached (${planSummary?.projects?.limit})`}
    >
      <span>
        <Button disabled>
          <Plus className="mr-2 h-4 w-4" />
          New project
        </Button>
      </span>
    </SimpleTooltip>
  ) : (
    <Button asChild>
      <Link to="/projects/new">
        <Plus className="mr-2 h-4 w-4" />
        New project
      </Link>
    </Button>
  );

  if (!projects || projects.length === 0) {
    return (
      <Page title="Projects" actions={<ActionBar primary={newProjectAction} />}>
        <div className="space-y-6">
          <LimitReachedBanner resource="storage" />
          <NoProjectsOnboarding apiUrl={cliApiUrl()} />
        </div>
      </Page>
    );
  }

  return (
    <Page
      title="Projects"
      description="Experience testing projects"
      actions={<ActionBar primary={newProjectAction} />}
    >
      <div className="space-y-6">
        <LimitReachedBanner resource="projects" />
        <LimitReachedBanner resource="storage" />

        {isMultiUser && shared.length > 0 ? (
          <div className="space-y-8">
            {owned.length > 0 && (
              <section>
                <h3 className="mb-3 text-sm font-medium text-muted-foreground">
                  My projects
                </h3>
                <div className="grid gap-6 lg:grid-cols-2">
                  {owned.map((project) => (
                    <ProjectCard key={project.id} project={project} />
                  ))}
                </div>
              </section>
            )}
            <section>
              <h3 className="mb-3 text-sm font-medium text-muted-foreground">
                Shared with me
              </h3>
              <div className="grid gap-6 lg:grid-cols-2">
                {shared.map((project) => (
                  <ProjectCard key={project.id} project={project} />
                ))}
              </div>
            </section>
          </div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-2">
            {projects.map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>
        )}
      </div>
    </Page>
  );
}

function ProjectCard({
  project,
}: {
  project: {
    id: string;
    name: string;
    description: string | null;
    createdAt: string | Date;
    role: string | null;
    access?: "member" | "team_owner" | null;
    memberCount?: number;
    scenarioCount?: number;
    walkthroughCount?: number;
    lastWalkthroughAt?: string | null;
    openFindingCount?: number;
  };
}) {
  const hasActivity = project.lastWalkthroughAt != null;
  const dateSource = hasActivity
    ? project.lastWalkthroughAt!
    : project.createdAt;
  const datePrefix = hasActivity ? "Active" : "Created";

  const scenarios = project.scenarioCount ?? 0;
  const walkthroughs = project.walkthroughCount ?? 0;
  const findings = project.openFindingCount ?? 0;

  return (
    <Link to={`/projects/${project.id}`} className="group">
      <Card className="h-full border border-border bg-card transition-all hover:border-primary/40 hover:bg-muted/10 hover:shadow-xs">
        <CardHeader>
          <div className="flex items-start justify-between gap-2">
            <CardTitle className="text-base font-semibold group-hover:text-primary transition-colors">
              {project.name}
            </CardTitle>
            {project.role && project.role !== "owner" && (
              <Badge variant="secondary" className="shrink-0 text-xs">
                {project.role}
              </Badge>
            )}
            {project.access === "team_owner" && (
              <SimpleTooltip content="You own this team, so you have owner access">
                <Badge
                  variant="secondary"
                  className="shrink-0 text-xs cursor-help"
                >
                  Team owner
                </Badge>
              </SimpleTooltip>
            )}
          </div>
          {project.description && (
            <CardDescription className="line-clamp-2">
              {project.description}
            </CardDescription>
          )}
        </CardHeader>
        <CardFooter className="flex-wrap gap-x-4 gap-y-1.5 text-xs text-muted-foreground border-t border-border/40 pt-3">
          <Timestamp date={dateSource} className="whitespace-nowrap">
            {datePrefix} {formatRelativeTime(dateSource)}
          </Timestamp>
          <span className="flex items-center gap-1 whitespace-nowrap">
            <Route className="size-3.5 shrink-0" />
            {scenarios} {scenarios === 1 ? "scenario" : "scenarios"}
          </span>
          <span className="flex items-center gap-1 whitespace-nowrap">
            <Play className="size-3.5 shrink-0" />
            {walkthroughs} {walkthroughs === 1 ? "run" : "runs"}
          </span>
          {findings > 0 ? (
            <span className="flex items-center gap-1 whitespace-nowrap text-severity-important font-medium">
              <AlertCircle className="size-3.5 shrink-0" />
              {findings} {findings === 1 ? "finding" : "findings"}
            </span>
          ) : (
            <span className="flex items-center gap-1 whitespace-nowrap text-finding-helps font-medium">
              <CheckCircle2 className="size-3.5 shrink-0" />
              Clean
            </span>
          )}
        </CardFooter>
      </Card>
    </Link>
  );
}
